import Keycloak from 'keycloak-js';

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'bpa',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'bpa-web',
};

export const keycloak = new Keycloak(keycloakConfig);

export interface KeycloakTokenParsed {
  exp?: number;
  iat?: number;
  sub?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  user_id?: number;
  company_id?: number;
  roles?: string[];
  realm_access?: {
    roles: string[];
  };
}

export function getTokenParsed(): KeycloakTokenParsed | undefined {
  return keycloak.tokenParsed as KeycloakTokenParsed | undefined;
}

export function hasRole(role: string): boolean {
  const tokenParsed = getTokenParsed();
  if (!tokenParsed) return false;

  const realmRoles = tokenParsed.realm_access?.roles || [];
  const customRoles = tokenParsed.roles || [];

  return realmRoles.includes(role) || customRoles.includes(role);
}

export function hasAnyRole(roles: string[]): boolean {
  return roles.some((role) => hasRole(role));
}

export function getCompanyId(): number | undefined {
  return getTokenParsed()?.company_id;
}

export function getUserId(): number | undefined {
  return getTokenParsed()?.user_id;
}

export async function initKeycloak(): Promise<boolean> {
  try {
    const authenticated = await keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    });

    if (authenticated) {
      setupTokenRefresh();
    }

    return authenticated;
  } catch (error) {
    console.error('Keycloak init error:', error);
    return false;
  }
}

function setupTokenRefresh(): void {
  setInterval(async () => {
    if (keycloak.authenticated) {
      try {
        const refreshed = await keycloak.updateToken(70);
        if (refreshed) {
          console.debug('Token refreshed');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        keycloak.login();
      }
    }
  }, 60000); // Check every minute
}

export function login(): void {
  keycloak.login();
}

export function logout(): void {
  keycloak.logout({ redirectUri: window.location.origin });
}

export function getToken(): string | undefined {
  return keycloak.token;
}
